import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import { useEffect, useState } from "react";
import { buscarContaDominioFiltros } from "../../services/api-conciliacao.service";

const ContaDominioFormSchema = z
  .object({
    descricao: z.string().trim().min(1, "Informe a descrição"),
    conta_contabil: z.string().trim().min(1, "Informe a conta domínio"),
    classificacao: z.string().trim().min(1, "Informe a classificação"),
  })
  .required({
    descricao: true,
    conta_contabil: true,
    classificacao: true,
  });

type ContaDominioFormData = z.infer<typeof ContaDominioFormSchema>;

type FiltroVinculadoRow = {
  id_conciliacao_filtro_vinculo_conta?: number;
  banco_label?: string | null;
  filtro?: string;
};

type ContaDominioFormProps = {
  contaDominioValues: Record<string, unknown> & {
    id_conciliacao_conta_contabil?: number;
    descricao?: string;
    conta_contabil?: string;
    classificacao?: string;
  };
  onSubmit(data: unknown): void;
  onClose(event: unknown): void;
};

export default function ContaDominioForm({
  contaDominioValues,
  onSubmit,
  onClose,
}: ContaDominioFormProps) {
  const [filtrosVinculados, setFiltrosVinculados] = useState<FiltroVinculadoRow[]>([]);
  const [loadingFiltros, setLoadingFiltros] = useState(false);

  const idConta = contaDominioValues.id_conciliacao_conta_contabil;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContaDominioFormData>({
    mode: "onChange",
    resolver: zodResolver(ContaDominioFormSchema),
    defaultValues: {
      descricao: contaDominioValues.descricao ?? "",
      conta_contabil: contaDominioValues.conta_contabil ?? "",
      classificacao: contaDominioValues.classificacao ?? "",
    },
  });

  useEffect(() => {
    reset({
      descricao: contaDominioValues.descricao ?? "",
      conta_contabil: contaDominioValues.conta_contabil ?? "",
      classificacao: contaDominioValues.classificacao ?? "",
    });
  }, [contaDominioValues, reset]);

  useEffect(() => {
    let cancelled = false;
    if (idConta == null || idConta === undefined) {
      setFiltrosVinculados([]);
      setLoadingFiltros(false);
      return;
    }
    setLoadingFiltros(true);
    (async () => {
      try {
        const res = await buscarContaDominioFiltros(idConta);
        const list = (res.filtrosVinculados ?? []) as FiltroVinculadoRow[];
        if (!cancelled) setFiltrosVinculados(list);
      } catch {
        if (!cancelled) setFiltrosVinculados([]);
      } finally {
        if (!cancelled) setLoadingFiltros(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idConta]);

  const handleCloseForm = (event: unknown) => {
    onClose(event);
  };

  const submit = (data: ContaDominioFormData) => {
    const body: Record<string, unknown> = {
      contaContabil: {
        id_conciliacao_conta_contabil: contaDominioValues.id_conciliacao_conta_contabil,
        descricao: data.descricao,
        contaContabil: data.conta_contabil,
        classificacao: data.classificacao,
      },
    };
    onSubmit(body);
  };

  const renderFiltrosSection = () => {
    if (idConta == null || idConta === undefined) {
      return (
        <Typography variant="body2" color="text.secondary" className={styles.filtrosVazio}>
          Salve a conta para visualizar os filtros vinculados.
        </Typography>
      );
    }
    if (loadingFiltros) {
      return (
        <Box className={styles.filtrosLoading}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Carregando filtros…
          </Typography>
        </Box>
      );
    }
    if (filtrosVinculados.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" className={styles.filtrosVazio}>
          Nenhum filtro vinculado a esta conta.
        </Typography>
      );
    }
    return (
      <TableContainer component={Paper} variant="outlined" className={styles.tabelaFiltros}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Banco</TableCell>
              <TableCell>Filtro</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtrosVinculados.map((row) => (
              <TableRow key={row.id_conciliacao_filtro_vinculo_conta ?? `${row.banco_label}-${row.filtro}`}>
                <TableCell>{row.banco_label ?? "—"}</TableCell>
                <TableCell>{row.filtro ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <Box
        className={styles.formContainer}
        sx={{ px: 3, pt: 1, pb: 2, width: "100%", boxSizing: "border-box" }}
      >
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="conta_contabil"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Conta Domínio"
                    error={Boolean(errors.conta_contabil)}
                    helperText={errors.conta_contabil?.message}
                    placeholder={"Conta Domínio"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="classificacao"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Classificação"
                    error={Boolean(errors.classificacao)}
                    helperText={errors.classificacao?.message}
                    placeholder={"Classificação"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="descricao"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    label="Descrição"
                    error={Boolean(errors.descricao)}
                    helperText={errors.descricao?.message}
                    placeholder={"Descrição"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" className={styles.filtrosTitulo}>
                Filtros vinculados
              </Typography>
              {renderFiltrosSection()}
            </Grid>
          </Grid>
        </Box>
        <Box className={styles.acoesForm}>
          <Button
            className={styles.botaoPopupCancelar}
            onClick={handleCloseForm}
            size="small"
          >
            Cancelar
          </Button>
          <Button
            className={styles.botaoPopupSalvar}
            type="submit"
            color="primary"
            size="small"
          >
            Salvar
          </Button>
        </Box>
      </Box>
    </form>
  );
}
